import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
// CSS
import "./order-form.css";
import { useCart } from "../../context/CartContext";
import { API_BASE_URL } from "../../config";
import { useDeliveryCharge } from "../../hooks/useDeliveryCharge";

const OrderForm = () => {
  const { cart, setCart } = useCart();
  // A cart only ever holds one restaurant's items at a time (see
  // SelectedMenuItem's conflict check), so the first item carrying a
  // restaurant is that restaurant.
  const cartRestaurantId = cart.find((item) => item.restaurant)?.restaurant;
  const baseDeliveryCharge = useDeliveryCharge(cartRestaurantId);

  const [form, setForm] = useState({
    name: "",
    address: "",
    phoneNumber: "",
    email: "",
    instructions: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const totalAmount = cart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );

    // Free delivery when the cart includes mango - used for the displayed
    // total and the analytics event below, not sent to or trusted by the
    // server (the server prices everything itself, including re-deriving
    // this same free-delivery-on-mango rule independently).
    const hasMango = cart.some(
      (item) => item.isMango || item._id?.startsWith("mango_"),
    );
    const deliveryCharge = hasMango ? 0 : baseDeliveryCharge;
    const grandTotal = totalAmount + deliveryCharge;

    // The server resolves both regular items and mango entries by id (see
    // utils/mangoResolver.js server-side) and prices everything itself, so
    // the whole cart - mixed or not - can just be sent as-is instead of
    // being split, summarized into free text, and partly dropped.
    const backendCart = cart.map((item) => ({
      id: item._id,
      category: item.category,
      restaurant: item.restaurant,
      quantity: item.quantity,
    }));

    const requestData = {
      name: form.name,
      address: form.address,
      phone: form.phoneNumber,
      email: form.email,
      request: form.instructions,
      cart: backendCart,
    };

    console.log("📦 Sending order data:", JSON.stringify(requestData, null, 2));

    try {
      const response = await axios.post(
        `${API_BASE_URL}/customer/send-message`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log("Order submitted successfully:", response.data);

      // Fires on a successful order submission, not on delivery/payment
      // confirmation - there's no payment gateway in this COD business
      // model, so "submitted" is used as a proxy for "purchase" today.
      // See docs/fixes.html Housekeeping section for the tradeoff this
      // implies for ad-spend optimization, and what a more accurate signal
      // would require.
      if (window.fbq && response.status === 200) {
        const contentIds = cart.map((item) => item._id);
        const contentNames = cart.map((item) => item.title);
        const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

        window.fbq("track", "Purchase", {
          value: grandTotal,
          currency: "BDT",
          content_ids: contentIds,
          content_names: contentNames,
          content_type: "product",
          num_items: totalItems,
          customer_name: form.name,
          customer_phone: form.phoneNumber,
          order_id: response.data.orderId || `order_${Date.now()}`,
        });

        console.log("🔥 Purchase event fired!", {
          value: grandTotal,
          items: totalItems,
          orderId: response.data.orderId || `order_${Date.now()}`,
        });
      }

      setForm({
        name: "",
        address: "",
        phoneNumber: "",
        email: "",
        instructions: "",
      });

      if (response.status === 200) {
        setCart([]);
        localStorage.removeItem("cart");
        localStorage.removeItem("cartTimestamp");
        navigate("/", {
          state: {
            success: "Order placed successfully! We will call you to confirm!",
          },
        });
      } else {
        toast("Failed to place order. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting order:", err);
      console.error("Error response:", err.response?.data);

      // Show detailed error message
      const errorMessage = err.response?.data?.message || err.message;
      setError(`Failed to place order: ${errorMessage}`);

      // If the error is about item not found, try a different approach
      if (
        errorMessage.includes("not found") ||
        errorMessage.includes("Item with ID")
      ) {
        setError(
          "Please ensure all items in your cart are valid. For mango orders, please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <section className="order-form-empty">
        <div className="order-form-container">
          <h3 className="order-form-heading">Your cart is empty</h3>
          <p className="order-form-description">
            Please add items to your cart before placing an order.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="order-form" id="order-form">
      <div className="order-form-container">
        <h3 className="order-form-heading">Order Delivery Details</h3>
        <p className="order-form-description">
          Please fill up this form to confirm your order and delivery
        </p>
        <p className="text-xl font-bold text-center pb-3">
          Once you have placed your order, our customer service will call you
          and confirm your order
        </p>
        <p className="text-xl font-bold text-center pb-3">
          {
            "(আপনি আপনার অর্ডার দেওয়ার পরে, আমাদের গ্রাহক পরিষেবা আপনাকে কল করবে এবং আপনার অর্ডার নিশ্চিত করবে)"
          }
        </p>
        {error && <p className="error-message">{error}</p>}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <form className="form" onSubmit={handleSubmit}>
          <div className="form-group name-group">
            <label htmlFor="name" className="form-label">
              Name*
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your name"
              className="form-input"
              required
            />
          </div>

          <div className="form-group address-group">
            <label htmlFor="address" className="form-label">
              Address*
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Your address"
              className="form-input"
              required
            />
          </div>

          <div className="form-group phone-number-group">
            <label htmlFor="phoneNumber" className="form-label">
              Phone Number*
            </label>
            <input
              type="text"
              id="phoneNumber"
              name="phoneNumber"
              value={form.phoneNumber}
              onChange={handleChange}
              placeholder="Your phone no"
              className="form-input"
              required
            />
          </div>

          <div className="form-group email-group">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              type="string"
              id="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Your email"
              className="form-input"
            />
          </div>

          <div className="form-group instructions-group">
            <label htmlFor="instructions" className="form-label">
              Requests / Message
            </label>
            <textarea
              id="instructions"
              name="instructions"
              value={form.instructions}
              onChange={handleChange}
              placeholder="Any special request / message you might have on the order or the delivery"
              className="form-textarea"
            ></textarea>
          </div>

          <button type="submit" className="form-btn btn btn-primary">
            {loading ? "Submitting..." : "Order"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default OrderForm;
